import { useState } from 'react';
import { useStore } from '../context/StoreContext';
import { Icon } from '../shared/ui/Icon';
import { formatPrice, getProductPrice, safeImage } from '../entities/product/helpers';

export function AccountPage() {
  const {
    user, orders, cartCount,
    go, signOut, browseCategory, updateUserProfile,
  } = useStore();

  const [activeTab, setActiveTab] = useState('overview'); // 'overview' | 'profile' | 'orders'
  const [profileName, setProfileName] = useState(user?.name || '');
  const [isUpdating, setIsUpdating] = useState(false);
  const [viewingOrder, setViewingOrder] = useState(null);

  async function handleProfileSave(e) {
    e.preventDefault();
    if (!profileName.trim()) return;
    setIsUpdating(true);
    try {
      await updateUserProfile({ name: profileName.trim() });
    } catch {
      // Handled in context
    } finally {
      setIsUpdating(false);
    }
  }

  return (
    <section className="flow-page account-page">
      <div className="breadcrumb">
        <button onClick={() => go('home')}>Home</button>
        <i>/</i>
        <span>My account</span>
      </div>

      <div className="flow-heading">
        <div>
          <p className="eyebrow">YOUR TECHNOMAX</p>
          <h1>Hello, {user?.name?.split(' ')[0] || 'there'}.</h1>
        </div>
        <button className="text-link" onClick={signOut}>
          Sign out <Icon>↗</Icon>
        </button>
      </div>

      <div className="account-layout">
        {/* ── Sidebar ── */}
        <nav className="account-nav">
          <span className="account-avatar">{user?.name?.[0]?.toUpperCase() || 'T'}</span>
          <b>{user?.name || 'User'}</b>
          <small>{user?.email}</small>

          <button
            className={activeTab === 'overview' ? 'selected' : ''}
            onClick={() => setActiveTab('overview')}
          >
            ▦ &nbsp; Overview
          </button>
          <button
            className={activeTab === 'profile' ? 'selected' : ''}
            onClick={() => setActiveTab('profile')}
          >
            ♙ &nbsp; My Profile
          </button>
          <button
            className={activeTab === 'orders' ? 'selected' : ''}
            onClick={() => setActiveTab('orders')}
          >
            ▤ &nbsp; Orders ({orders.length})
          </button>

          {user?.role === 'admin' && (
            <button
              className="admin-sidebar-link"
              onClick={() => go('admin')}
            >
              👑 &nbsp; Admin Control Suite ↗
            </button>
          )}
        </nav>

        {/* ── Content ── */}
        <div className="account-content">
          {/* Admin callout banner if role is admin */}
          {user?.role === 'admin' && activeTab === 'overview' && (
            <div className="admin-banner-card">
              <div>
                <span className="admin-banner-tag">👑 ADMINISTRATOR ACCESS</span>
                <h3>Technomax Management Suite</h3>
                <p>Manage products, fulfill orders, review user accounts, and customize store settings.</p>
              </div>
              <button className="button button-primary" onClick={() => go('admin')}>
                Launch Admin Panel <Icon>→</Icon>
              </button>
            </div>
          )}

          {activeTab === 'overview' && (
            <>
              <div className="account-welcome">
                <div>
                  <p className="eyebrow">ACCOUNT OVERVIEW</p>
                  <h2>Your next good thing starts here.</h2>
                  <p>Keep track of your orders, delivery status, and personal details.</p>
                </div>
                <span className="welcome-symbol">↗</span>
              </div>

              <div className="orders-panel">
                <div className="panel-heading">
                  <h2>Recent orders</h2>
                  <span>{orders.length} total</span>
                </div>
                {orders.length ? (
                  orders.slice(0, 5).map((order) => (
                    <div
                      className="order-row clickable-order-row"
                      key={order.id}
                      onClick={() => setViewingOrder(order)}
                    >
                      <div>
                        <b>Order #{String(order.id).slice(-7).toUpperCase()}</b>
                        <small>{new Date(order.createdAt).toLocaleDateString()}</small>
                      </div>
                      <span className={`status status-${String(order.status).toLowerCase()}`}>
                        {order.status}
                      </span>
                      <b>{formatPrice(order.total)}</b>
                    </div>
                  ))
                ) : (
                  <div className="empty-orders">
                    <span>▤</span>
                    <div>
                      <b>Nothing here just yet.</b>
                      <p>Your orders will appear here once you place an order.</p>
                    </div>
                    <button className="text-link" onClick={() => browseCategory()}>Shop now ↗</button>
                  </div>
                )}
              </div>

              <div className="account-shortcuts">
                <button onClick={() => go('cart')}>
                  <span>▱</span><b>Your bag</b><small>{cartCount} items</small><i>↗</i>
                </button>
                <button onClick={() => browseCategory()}>
                  <span>⌕</span><b>Explore</b><small>Find your next favorite</small><i>↗</i>
                </button>
              </div>
            </>
          )}

          {activeTab === 'profile' && (
            <div className="account-profile-card">
              <div className="panel-heading">
                <h2>Personal Details</h2>
                <span className={`role-badge ${user?.role === 'admin' ? 'role-admin' : 'role-customer'}`}>
                  {user?.role === 'admin' ? '👑 Admin' : 'Customer Account'}
                </span>
              </div>
              <form onSubmit={handleProfileSave} className="profile-form">
                <label>
                  Full Name
                  <input
                    value={profileName}
                    onChange={(e) => setProfileName(e.target.value)}
                    required
                    placeholder="Your Name"
                  />
                </label>
                <label>
                  Email Address
                  <input value={user?.email || ''} disabled title="Email cannot be changed" />
                  <small className="field-hint">Email is linked to your login identity.</small>
                </label>
                <button className="button button-primary" type="submit" disabled={isUpdating}>
                  {isUpdating ? 'Saving...' : 'Update Profile'} <Icon>→</Icon>
                </button>
              </form>
            </div>
          )}

          {activeTab === 'orders' && (
            <div className="orders-panel">
              <div className="panel-heading">
                <h2>All Orders</h2>
                <span>{orders.length} total orders</span>
              </div>
              {orders.length ? (
                orders.map((order) => (
                  <div
                    className="order-row clickable-order-row"
                    key={order.id}
                    onClick={() => setViewingOrder(order)}
                  >
                    <div>
                      <b>Order #{String(order.id).slice(-8).toUpperCase()}</b>
                      <small>{new Date(order.createdAt).toLocaleDateString()} · {(order.items || []).length} items</small>
                    </div>
                    <span className={`status status-${String(order.status).toLowerCase()}`}>
                      {order.status}
                    </span>
                    <b>{formatPrice(order.total)}</b>
                  </div>
                ))
              ) : (
                <div className="empty-orders">
                  <span>▤</span>
                  <div>
                    <b>No order history found.</b>
                    <p>Start discovering items in our collection.</p>
                  </div>
                  <button className="text-link" onClick={() => browseCategory()}>Explore collection ↗</button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* ── Order details modal ── */}
      {viewingOrder && (
        <div className="modal-backdrop" onMouseDown={(e) => { if (e.target === e.currentTarget) setViewingOrder(null); }}>
          <div className="admin-modal order-detail-modal">
            <button className="modal-close" onClick={() => setViewingOrder(null)}>×</button>
            <p className="eyebrow">ORDER DETAILS</p>
            <h2>Order #{String(viewingOrder.id).slice(-8).toUpperCase()}</h2>
            <p className="order-date-label">Placed on {new Date(viewingOrder.createdAt).toLocaleString()}</p>

            <div className="order-modal-grid">
              <div className="order-modal-box">
                <h4>Shipping Address</h4>
                <p><b>Name:</b> {viewingOrder.shippingAddress?.name || '-'}</p>
                <p><b>Phone:</b> {viewingOrder.shippingAddress?.phone || '-'}</p>
                <p><b>Email:</b> {viewingOrder.shippingAddress?.email || '-'}</p>
                <p><b>Address:</b> {viewingOrder.shippingAddress?.address || '-'}</p>
                <p><b>City / Region:</b> {viewingOrder.shippingAddress?.city || 'Tbilisi'} ({viewingOrder.deliveryLocation || 'Tbilisi'})</p>
              </div>

              <div className="order-modal-box">
                <h4>Status & Payment</h4>
                <p><b>Order Status:</b> <span className={`status status-${String(viewingOrder.status).toLowerCase()}`}>{viewingOrder.status}</span></p>
                <p><b>Payment Method:</b> {viewingOrder.paymentMethod || 'Card'}</p>
                <p><b>Payment Status:</b> {viewingOrder.paymentStatus || 'pending'}</p>
              </div>
            </div>

            <div className="order-modal-items">
              <h4>Ordered Items ({(viewingOrder.items || []).length})</h4>
              {(viewingOrder.items || []).map((item, idx) => (
                <div className="order-modal-line" key={idx}>
                  <img src={safeImage(item)} alt="" />
                  <div>
                    <b>{item.title || item.name}</b>
                    <small>Qty {item.qty || 1} × {formatPrice(getProductPrice(item))}</small>
                  </div>
                  <strong>{formatPrice(getProductPrice(item) * Number(item.qty || 1))}</strong>
                </div>
              ))}
              <div className="order-modal-total">
                <span>Total Amount</span>
                <strong>{formatPrice(viewingOrder.total)}</strong>
              </div>
            </div>

            <div className="modal-actions">
              <button className="button button-primary" onClick={() => setViewingOrder(null)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
